import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Chat, { Bubble, useMessages, toast } from '@chatui/core';
import logisticsConversationStorage from '../../utils/logisticsConversationStorage';
import './LogisticsChat.css';
import renderMessageContent from "../../utils/renderMessage";


const assistantInfo = {
  avatar: '/images/tangxiaoer.png',
  name: "物流助手",
};

const baseUrl = 'https://ai-merchant-sit-api.wormwood.ltd';

const LogisticsChat = ({ onBack, user }) => {
  const { t, i18n } = useTranslation();
  // const [currentAssistant, setCurrentAssistant] = useState('logistics');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [isNewConversation, setIsNewConversation] = useState(true);
  
  const getInitialMessages = () => [
    {
      type: 'text',
      content: { 
        text: t('chat.logisticsAssistantGreeting', '您好！我是物流助手，可以帮您处理物流配送、订单追踪、运费计算等问题。请问有什么可以帮助您的？'),
        isRich: true
      },
      user: assistantInfo
    }
  ];

  const initialMessages = getInitialMessages();
  const { messages, appendMsg, updateMsg, deleteMsg, resetList } = useMessages(initialMessages);

  const generateConversationTitle = (firstMessage) => {
    if (!firstMessage || !firstMessage.content) return t('logistics.newChat', '新对话');
    
    const text = typeof firstMessage.content === 'string' 
      ? firstMessage.content 
      : firstMessage.content.text || '';
    
    const title = text.trim().substring(0, 50);
    return title || t('logistics.newChat', '新对话');
  };

  useEffect(() => {
    const loadConversations = async () => {
      try {
        const conversations = await logisticsConversationStorage.getAllConversations();
        setConversations(conversations);
        
        if (conversations.length > 0 && !currentConversationId) {
          const firstConv = conversations[0];
          setCurrentConversationId(firstConv.id);
          setIsNewConversation(false);
          if (firstConv.messages && firstConv.messages.length > 0) {
            resetList(firstConv.messages);
          }
        }
      } catch (error) {
        console.error('Failed to load logistics conversations:', error);
      }
    };
    
    loadConversations();
  }, []);

  const handleNewChat = async () => {
    const userMessages = messages.filter(m => m.position === 'right');
    if (userMessages.length > 0) {
      await saveConversationToHistory();
    }
    resetList(getInitialMessages());
    setCurrentConversationId(null);
    setIsNewConversation(true);
    setSidebarOpen(false);
  };

  const handleSelectConversation = async (conversationId) => {
    try {
      const conversation = await logisticsConversationStorage.getConversation(conversationId);
      if (conversation) {
        resetList(conversation.messages || getInitialMessages());
        setCurrentConversationId(conversationId);
        setIsNewConversation(false);
        setSidebarOpen(false);
      }
    } catch (error) {
      console.error('Failed to load logistics conversation:', error);
    }
  };

  const handleDeleteConversation = async (conversationId) => {
    try {
      const success = await logisticsConversationStorage.deleteConversation(conversationId);
      if (success) {
        const newConversations = conversations.filter(c => c.id !== conversationId);
        setConversations(newConversations);
        
        if (currentConversationId === conversationId) {
          if (newConversations.length > 0) {
            handleSelectConversation(newConversations[0].id);
          } else {
            handleNewChat();
          }
        }
      }
    } catch (error) {
      console.error('Error deleting logistics conversation:', error);
    }
  };

  const saveConversationToHistory = async () => {
    const userMessages = messages.filter(m => m.position === 'right');
    if (userMessages.length === 0) return;
    
    const firstUserMessage = userMessages[0];
    const title = generateConversationTitle(firstUserMessage);
    
    const conversationId = currentConversationId || Date.now().toString();
    const conversation = {
      id: conversationId,
      title: title,
      preview: firstUserMessage?.content?.text?.substring(0, 100) || firstUserMessage?.content?.text || '',
      messages: [...messages],
      updatedAt: Date.now()
    };

    try {
      const success = await logisticsConversationStorage.saveConversation(conversation);
      if (success) {
        const newConversations = [conversation, ...conversations.filter(c => c.id !== conversationId)];
        setConversations(newConversations);
      }
    } catch (error) {
      console.error('Error saving logistics conversation:', error);
    }
  };

  const saveCurrentConversation = async () => {
    if (!currentConversationId) return;
    
    const firstUserMessage = messages.find(m => m.position === 'right');
    if (!firstUserMessage) return;
    
    const title = generateConversationTitle(firstUserMessage);
    
    const conversation = {
      id: currentConversationId,
      title: title,
      preview: firstUserMessage?.content?.text?.substring(0, 100) || '',
      messages: messages,
      updatedAt: Date.now()
    };

    try {
      const success = await logisticsConversationStorage.saveConversation(conversation);
      if (success) {
        const existingIndex = conversations.findIndex(c => c.id === currentConversationId);
        let newConversations;
        
        if (existingIndex >= 0) {
          newConversations = [...conversations];
          newConversations[existingIndex] = conversation;
        } else {
          newConversations = [conversation, ...conversations];
        }
        
        newConversations.sort((a, b) => b.updatedAt - a.updatedAt);
        setConversations(newConversations);
      }
    } catch (error) {
      console.error('Error saving current logistics conversation:', error);
    }
  };

  useEffect(() => {
    const userMessages = messages.filter(m => m.position === 'right');
    if (currentConversationId && userMessages.length > 0) {
      const timer = setTimeout(async () => {
        await saveCurrentConversation();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [messages, currentConversationId]);

  const handleSend = useCallback((type, val) => {
    if (type === 'text' && val.trim()) {
      appendMsg({
        type: 'text',
        content: { text: val },
        position: 'right',
      });

      const loadingMsgId = appendMsg({
        type: 'loading',
        content: { text: '' },
        position: 'left',
        user: assistantInfo
      });

      fetch(`${baseUrl}/api/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': user? user.token: 'guest_token'
        },
        body: JSON.stringify({
          user: user? user.userId: 'guest'+Date.now(),
          user_type: "merchant",
          token: user? user.token: 'guest_token',
          query: val,
          chatbot_name: 'tangxiaoer', // 默认聊天机器人名称
          conversation_id: currentConversationId || '', // 如果没有会话ID则为空
          image_infos: [], // 如果没有图片则为空数组
          file_infos: [], // 如果没有文件则为空数组
          intention: "logistics"
        })
      })
        .then(response => {
          deleteMsg(loadingMsgId);
          
          if (!response.body) {
            throw new Error('Response body is not available');
          }
          
          const decoder = new TextDecoder('utf-8');
          const reader = response.body.getReader();
          
          let accumulatedResponse = '';
          let fullMessageContent = '';
          
          const botMessageId = appendMsg({
            type: 'typingBubble',
            content: { text: '', isLoading: true },
            position: 'left',
            user: assistantInfo
          });
          
          const readStream = () => {
            return reader.read().then(({ done, value }) => {
              if (done) {
                updateMsg(botMessageId, {
                  type: 'text',
                  content: { text: fullMessageContent, isRich: true },
                  position: 'left',
                  user: assistantInfo
                });
                return;
              }
              
              const chunk = decoder.decode(value, { stream: true });
              accumulatedResponse += chunk;
              
              try {
                const lines = accumulatedResponse.split('\n');
                
                for (let i = 0; i < lines.length - 1; i++) {
                  if (lines[i].trim()) {
                    const jsonData = JSON.parse(lines[i]);
                    
                    if (jsonData['conversation_id'] && !currentConversationId) {
                      setCurrentConversationId(jsonData['conversation_id']);
                      setIsNewConversation(false);
                    }
                    
                    if (jsonData.event === 'message' && jsonData.answer) {
                      fullMessageContent += jsonData.answer;
                      
                      updateMsg(botMessageId, {
                        type: 'typingBubble',
                        content: { text: fullMessageContent, isLoading: true },
                        position: 'left',
                        user: assistantInfo
                      });
                    }
                  }
                }
                
                accumulatedResponse = lines[lines.length - 1];
              } catch (error) {
              }
              
              return readStream();
            });
          };
          
          return readStream();
        })
        .catch(error => {
          deleteMsg(loadingMsgId);
          appendMsg({
            type: 'text',
            content: { text: i18n.language === 'zh' ? '抱歉，出现了一些问题，请稍后再试。' : 'Sorry, something went wrong. Please try again later.' },
            position: 'left',
            user: assistantInfo
          });
        });
    }
  }, [appendMsg, deleteMsg, updateMsg, user, i18n.language, currentConversationId]);

  const handleBack = () => {
    if (onBack) {
      onBack();
    }
  };

  const handleToggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div className="logistics-chat-container">
      <div className="chat-header">
        <button 
          className="chat-back-btn" 
          onClick={handleBack}
          type="button"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
        <div className="chat-header-info">
          <div className="chat-header-avatar">
            <img src={assistantInfo.avatar} alt={assistantInfo.name} />
          </div>
          <div className="chat-header-text">
            <h2 className="chat-header-title">{assistantInfo.name}</h2>
            <span className="chat-header-status">在线</span>
          </div>
        </div>
        <button 
          className="chat-menu-btn" 
          onClick={handleToggleSidebar}
          type="button"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="12" x2="21" y2="12"></line>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
        </button>
      </div>

      <div className="chat-content-wrapper">
        <Chat
          messages={messages}
          renderMessageContent={(msg) => renderMessageContent(t, msg)}
          onSend={handleSend}
          locale={i18n.language === 'zh' ? 'zh-CN' : 'en-US'}
          navbar={{
            left: <></>,
            right: <></>
          }}
        />
      </div>

      {sidebarOpen && (
        <div className="logistics-sidebar-overlay" onClick={handleToggleSidebar}>
          <div className="logistics-sidebar" onClick={(e) => e.stopPropagation()}>
            <div className="logistics-sidebar-header">
              <h3 className="logistics-sidebar-title">{t('logistics.conversations', '对话历史')}</h3>
              <button 
                className="logistics-sidebar-close" 
                onClick={handleToggleSidebar}
                type="button"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            
            <button 
              className="logistics-new-chat-btn"
              onClick={handleNewChat}
              type="button"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              {t('logistics.newChat', '新对话')}
            </button>

            <div className="logistics-conversations-list">
              {conversations.length === 0 ? (
                <div className="logistics-empty-state">
                  <p>{t('logistics.noConversations', '暂无对话历史')}</p>
                </div>
              ) : (
                conversations.map(conversation => (
                  <div 
                    key={conversation.id}
                    className={`logistics-conversation-item ${currentConversationId === conversation.id ? 'active' : ''}`}
                    onClick={() => handleSelectConversation(conversation.id)}
                  >
                    <div className="logistics-conversation-content">
                      <div className="logistics-conversation-title">{conversation.title}</div>
                      <div className="logistics-conversation-preview">{conversation.preview}</div>
                    </div>
                    <button 
                      className="logistics-conversation-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteConversation(conversation.id);
                      }}
                      type="button"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LogisticsChat;
